import { AppConstants } from '@/app.constants';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { LocationEvidence, LocationVerificationDraft } from '@/types/LocationVerification.type';
import { ensureDB } from './idbStorage';

const DRAFT_STORE = AppConstants.INDEX_DB.store.locationVerificationDrafts;
const IMAGE_STORE = AppConstants.INDEX_DB.store.images;

function buildImageKey(inspectionId: string, evidenceId: string): string {
  return `${inspectionId}_${InspectionImageType.VERIFICATION}_${IMAGE_STORE}_${evidenceId}`;
}

export const locationVerificationDraftStorage = {
  get: async (inspectionId: string): Promise<LocationVerificationDraft | null> => {
    const db = await ensureDB();
    const record = await db.get(DRAFT_STORE, inspectionId);
    return record ?? null;
  },

  put: async (draft: LocationVerificationDraft): Promise<void> => {
    const db = await ensureDB();
    await db.put(DRAFT_STORE, { ...draft, updatedAt: new Date().toISOString() });
  },

  remove: async (inspectionId: string): Promise<void> => {
    const db = await ensureDB();
    await db.delete(DRAFT_STORE, inspectionId);
  },

  getImageData: async (inspectionId: string, evidenceId: string): Promise<string | null> => {
    const db = await ensureDB();
    const key = buildImageKey(inspectionId, evidenceId);
    return (await db.get(IMAGE_STORE, key)) ?? null;
  },

  saveEvidenceCapture: async (params: {
    draft: LocationVerificationDraft;
    evidence: LocationEvidence;
    imageData: string;
  }): Promise<void> => {
    const db = await ensureDB();
    const tx = db.transaction([DRAFT_STORE, IMAGE_STORE], 'readwrite');
    const imageKey = buildImageKey(params.draft.inspectionId, params.evidence.clientEvidenceId);
    await tx.objectStore(IMAGE_STORE).put(params.imageData, imageKey);
    await tx.objectStore(DRAFT_STORE).put({
      ...params.draft,
      updatedAt: new Date().toISOString(),
    });
    await tx.done;
  },

  removeEvidence: async (params: {
    draft: LocationVerificationDraft;
    clientEvidenceId: string;
  }): Promise<void> => {
    const db = await ensureDB();
    const tx = db.transaction([DRAFT_STORE, IMAGE_STORE], 'readwrite');
    const imageKey = buildImageKey(params.draft.inspectionId, params.clientEvidenceId);
    await tx.objectStore(IMAGE_STORE).delete(imageKey);
    const nextDraft: LocationVerificationDraft = {
      ...params.draft,
      evidence: params.draft.evidence.filter(
        (item) => item.clientEvidenceId !== params.clientEvidenceId
      ),
      updatedAt: new Date().toISOString(),
    };
    await tx.objectStore(DRAFT_STORE).put(nextDraft);
    await tx.done;
  },

  clearForInspector: async (inspectorId: string): Promise<void> => {
    const db = await ensureDB();
    const allDrafts = (await db.getAll(DRAFT_STORE)) as LocationVerificationDraft[];
    const toRemove = allDrafts.filter((draft) => draft.inspectorId === inspectorId);
    if (toRemove.length === 0) {
      return;
    }
    const tx = db.transaction([DRAFT_STORE, IMAGE_STORE], 'readwrite');
    for (const draft of toRemove) {
      await tx.objectStore(DRAFT_STORE).delete(draft.inspectionId);
      for (const evidence of draft.evidence) {
        const imageKey = buildImageKey(draft.inspectionId, evidence.clientEvidenceId);
        await tx.objectStore(IMAGE_STORE).delete(imageKey);
      }
    }
    await tx.done;
  },

  markEvidenceUploaded: async (params: {
    inspectionId: string;
    clientEvidenceId: string;
    imageUrl?: string;
  }): Promise<LocationVerificationDraft | null> => {
    const draft = await locationVerificationDraftStorage.get(params.inspectionId);
    if (!draft) {
      return null;
    }
    const nextEvidence = draft.evidence.map((item) =>
      item.clientEvidenceId === params.clientEvidenceId
        ? {
            ...item,
            uploadState: 'UPLOADED' as const,
            ...(params.imageUrl ? { imageUrl: params.imageUrl } : {}),
          }
        : item
    );
    const nextDraft: LocationVerificationDraft = {
      ...draft,
      evidence: nextEvidence,
      updatedAt: new Date().toISOString(),
    };
    await locationVerificationDraftStorage.put(nextDraft);
    return nextDraft;
  },
};
